import { FC, ReactNode } from 'react';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Typography,
    Slide,
    useTheme,
    useMediaQuery,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { TransitionProps } from '@mui/material/transitions';
import React from 'react';

const Transition = React.forwardRef(function Transition(
    props: TransitionProps & {
        children: React.ReactElement<any, any>;
    },
    ref: React.Ref<unknown>
) {
    return <Slide direction="up" ref={ref} {...props} />;
});

export interface ModalProps {
    open: boolean;
    onClose: () => void;
    title: string;
    children: ReactNode;
    actions?: ReactNode;
    maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
    fullWidth?: boolean;
    fullScreen?: boolean;
    disableBackdropClick?: boolean;
    showCloseButton?: boolean;
    dividers?: boolean;
}

export const Modal: FC<ModalProps> = ({
    open,
    onClose,
    title,
    children,
    actions,
    maxWidth = 'sm',
    fullWidth = true,
    fullScreen = false,
    disableBackdropClick = false,
    showCloseButton = true,
    dividers = true,
}) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleClose = (_event: any, reason: string) => {
        if (disableBackdropClick && reason === 'backdropClick') {
            return;
        }
        onClose();
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            TransitionComponent={Transition}
            maxWidth={maxWidth}
            fullWidth={fullWidth}
            fullScreen={fullScreen || isMobile}
            PaperProps={{
                sx: {
                    borderRadius: fullScreen || isMobile ? 0 : 2,
                    maxHeight: '90vh',
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    py: 2,
                    px: 3,
                }}
            >
                <Typography variant="h6" component="div" sx={{ fontWeight: 600 }}>
                    {title}
                </Typography>
                {showCloseButton && (
                    <IconButton
                        aria-label="close"
                        onClick={onClose}
                        sx={{
                            color: 'inherit',
                            '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                            },
                        }}
                    >
                        <CloseIcon />
                    </IconButton>
                )}
            </DialogTitle>
            <DialogContent
                dividers={dividers}
                sx={{
                    p: 3,
                    backgroundColor: 'background.default',
                }}
            >
                {children}
            </DialogContent>
            {actions && (
                <DialogActions
                    sx={{
                        p: 2,
                        px: 3,
                        gap: 1,
                        backgroundColor: 'background.paper',
                    }}
                >
                    {actions}
                </DialogActions>
            )}
        </Dialog>
    );
};

